import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useInView } from 'react-intersection-observer';

/*
 =================================================================================================
 =================================== IMPORTANT SEO INSTRUCTIONS ==================================
 =================================================================================================

 To make this page fully SEO-friendly, you MUST add the following code to the `<head>` 
 section of your project's main HTML file.
 
 This file is usually located at: `public/index.html`

 For more advanced SEO in React (like changing the title for each page), 
 it is highly recommended to use a library like "react-helmet-async".

 =================================================================================================
 === STEP 1: INSTALL REQUIRED LIBRARIES ==========================================================
 =================================================================================================
 
 This component now uses `react-intersection-observer` for scroll animations. 
 Please install it by running this command in your project terminal:

 npm install react-intersection-observer

 =================================================================================================
 === STEP 2: COPY AND PASTE THIS ENTIRE BLOCK INTO YOUR `public/index.html` <head> SECTION ===
 =================================================================================================
*/
/*
    <!-- Primary SEO Meta Tags -->
    <title>Copper Boss: IT Solutions, Software, Hardware & Support</title>
    <meta name="description" content="Your trusted partner for end-to-end IT solutions. From bespoke software development and chip-level repairs to branded laptops, custom PCs, and IT internships.">
    <meta name="keywords" content="IT solutions, custom software, laptop repair, chip-level service, branded laptops, custom PC builds, IT support, software installation, IT internships, Copper Boss">
    <meta name="author" content="Copper Boss">
    <meta name="robots" content="index, follow">

    <!-- Open Graph Meta Tags (for social sharing on Facebook, LinkedIn, etc.) -->
    <meta property="og:title" content="Copper Boss: IT Solutions, Software, Hardware & Support">
    <meta property="og:description" content="End-to-end IT solutions including software development, hardware repairs, custom PCs, and professional training.">
    <meta property="og:image" content="https://placehold.co/1200x630/2a006c/ffffff?text=Copper+Boss">
    <meta property="og:url" content="https://your-website-url.com">
    <meta property="og:type" content="website">

    <!-- Twitter Card Meta Tags -->
    <meta name="twitter:card" content="summary_large_image">
    <meta name="twitter:title" content="Copper Boss: IT Solutions, Software, Hardware & Support">
    <meta name="twitter:description" content="End-to-end IT solutions including software development, hardware repairs, custom PCs, and professional training.">
    <meta name="twitter:image" content="https://placehold.co/1200x630/2a006c/ffffff?text=Copper+Boss">

    <!-- Favicon -->
    <link rel="icon" href="https://placehold.co/32x32/007bff/ffffff?text=CB" type="image/png">

    <!-- JSON-LD Structured Data for Rich Snippets in Google -->
    <script type="application/ld+json">
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Copper Boss",
      "image": "https://placehold.co/600x400/2a006c/ffffff?text=Copper+Boss+Logo",
      "description": "Your trusted partner in IT, offering a comprehensive range of services including bespoke software development, chip-level hardware repairs, branded laptops, custom PC builds, and professional IT training.",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Your Street Address",
        "addressLocality": "Your City",
        "addressRegion": "Your State",
        "postalCode": "Your Postal Code",
        "addressCountry": "IN"
      },
      "telephone": "+91-YOUR-NUMBER-HERE",
      "url": "https://your-website-url.com",
      "priceRange": "$$",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "09:00",
          "closes": "19:00"
        }
      ],
      "hasOffer": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Bespoke Software Development" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Chip-Level Laptop Repairs" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Premium Laptop Sales" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "IT Accessories & Peripherals" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Cloud Solutions" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Network Security" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Data Recovery" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "IT Consulting" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Web Development" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mobile App Development" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "UI/UX Design" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Digital Marketing" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "IT Training & Internships" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Server Management" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "E-commerce Solutions" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "24/7 Tech Support" } }
      ]
    }
    </script>
*/
// =================================================================================================
// ============================= END OF SEO INSTRUCTIONS ===========================================
// =================================================================================================


const StyledPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(5px)',
  border: '1px solid rgba(255, 255, 255, 0.4)',
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(4),
  boxShadow: theme.shadows[15],
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.4s ease-out, box-shadow 0.4s ease-out, background 0.4s ease-out',
  color: theme.palette.common.white,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '300px',
  height: '300px',
  margin: '0 auto',

  '&:hover': {
    transform: 'translateY(-15px) scale(1.03)',
    boxShadow: theme.shadows[24],
    background: 'rgba(255, 255, 255, 0.35)',
  },

  '&::before': {
    content: '""',
    position: 'absolute',
    top: -2, left: -2, right: -2, bottom: -2,
    background: 'linear-gradient(45deg, #00c6ff, #0072ff, #f000ff)',
    zIndex: -1,
    borderRadius: (theme.shape.borderRadius * 2) + 2,
    opacity: 0,
    transition: 'opacity 0.4s ease-out, transform 0.4s ease-out',
    filter: 'blur(8px)',
  },
  '&:hover::before': {
    opacity: 1,
    transform: 'scale(1.05)',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 70%)',
    borderRadius: theme.shape.borderRadius * 2,
    opacity: 0,
    transition: 'opacity 0.4s ease-out',
  },
  '&:hover::after': {
    opacity: 1,
  },
}));

// This new component wraps each card to handle the scroll animation
const AnimatedCard = ({ children, index }) => {
  const { ref, inView } = useInView({
    triggerOnce: true, // Animation will trigger only once
    threshold: 0.1,    // Trigger when 10% of the card is visible
  });

  return (
    <Box
      ref={ref}
      sx={{
        // Smooth transition for opacity and transform
        transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
        // Stagger the animation for each card based on its index
        transitionDelay: `${index * 100}ms`,
        // Start with card being transparent and slightly moved down
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(40px)',
      }}
    >
      {children}
    </Box>
  );
};

const HeroSection = () => {
  const services = [
    { title: "Bespoke Software", description: "Scalable, secure, and custom-built software crafted to align perfectly with your business goals." },
    { title: "Chip Level Laptop Repairs", description: "Precision diagnostics and expert chip-level repairs for laptops, desktops, and logic boards." },
    { title: "Premium Laptops", description: "Wide range of branded laptops – DELL, HP, Lenovo, Acer, ASUS – with tailored specifications and warranty." },
    { title: "IT Accessories", description: "Genuine accessories and peripherals for all your computing needs, with expert support and advice." },
    { title: "Cloud Solutions", description: "Scalable cloud infrastructure, seamless migrations, and secure deployment tailored to your workflow." },
    { title: "Network Security", description: "Enterprise-grade cybersecurity services including threat monitoring, firewalls, and data protection." },
    { title: "Data Recovery", description: "Fast and secure recovery of lost or corrupted data from hard drives, SSDs, and external devices." },
    { title: "IT Consulting", description: "Insightful IT strategy and planning to align technology with your business growth and operations." },
    { title: "Web Development", description: "Engaging and responsive websites built on modern frameworks with seamless user experience." },
    { title: "Mobile Apps", description: "High-performance cross-platform mobile apps designed for reliability and smooth user engagement." },
    { title: "UI/UX Design", description: "Modern, user-centric interfaces that enhance usability, accessibility, and brand identity." },
    { title: "Digital Marketing", description: "ROI-driven strategies including SEO, PPC, and social media marketing to grow your digital presence." },
    { title: "IT Training", description: "Hands-on IT training programs, workshops, and certified internships for career development." },
    { title: "Server Management", description: "Reliable server deployment, monitoring, patching, and performance tuning for maximum uptime." },
    { title: "E-commerce", description: "Robust e-commerce platforms with integrated payment systems, inventory tools, and analytics." },
    { title: "Tech Support", description: "24/7 technical support to resolve system issues, optimize performance, and ensure business continuity." },
  ];

  return (
    <Box
      component="section"
      sx={{
        background: 'linear-gradient(135deg, #2a006c, #007bff)',
        color: 'white',
        padding: { xs: '80px 16px', md: '100px 20px' },
        textAlign: 'center',
        overflowX: 'hidden', // Prevent horizontal scrollbar during animation
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box sx={{ maxWidth: '1600px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
            marginBottom: 2,
            letterSpacing: '1.5px',
            textShadow: '2px 2px 8px rgba(0, 0, 0, 0.3)',
            fontWeight: 700,
          }}
        >
          Your Trusted Partner in IT: Software, Hardware & End-to-End Support
        </Typography>

        <Typography
          variant="h6"
          component="p"
          sx={{
            fontSize: { xs: '1rem', md: '1.3rem' },
            marginBottom: 5,
            opacity: 0.9,
            lineHeight: 1.6,
            maxWidth: '800px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          From cutting-edge Software Development to expert Chip-Level Laptop Service, we deliver end-to-end IT solutions — including Branded Laptops, Custom PC Builds, Professional Software Installation, and Skill-Building Internship Opportunities.
        </Typography>

        <Grid
          container
          spacing={4}
          justifyContent="center"
          sx={{ marginTop: 5 }}
        >
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index} sx={{ display: 'flex', justifyContent: 'center' }}>
              <AnimatedCard index={index}>
                <StyledPaper>
                  <Typography variant="h5" component="h2" sx={{ marginBottom: 2, fontWeight: 600 }}>
                    {service.title}
                  </Typography>
                  <Typography variant="body1">
                    {service.description}
                  </Typography>
                </StyledPaper>
              </AnimatedCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default HeroSection;
